/**
 * The place under a tap, and what the publishers say about it.
 *
 * Which area a tap landed in is answered in two steps. `propmap prod` did the
 * point-in-polygon for all 4,994 LSOAs once on a 100 m raster, so the client can
 * say which areas are near a tap without geometry, without a request and
 * without waiting for the outlines. A 100 m cell straddles borders, though, so
 * once the outlines are in hand the tap is tested against the few areas near it
 * -- decoding only those -- and the answer agrees with the outline drawn.
 *
 * The figures themselves are the publishers' own, as `propmap.official`
 * fetched them: MHCLG's deprivation deciles, the Met's recorded offences, ONS's
 * median price, DfT's connectivity. Nothing here ranks, blends or smooths them.
 * The two computed lines -- the closest school, the nearest station -- are
 * distances over the lists this project already holds, and they say so.
 *
 * The four category scores in the panel are not here: they are read from the
 * baked rasters at the tapped point, which is the same number the colour under
 * the reader's finger was drawn from.
 */

import type { Data, Schools } from "./data.ts";
import { decodePolyline } from "./links.ts";
import type { AreaFigures, AreaOutlines, Manifest } from "./manifest.ts";
import { metresBetween } from "./project.ts";
import type { Projector } from "./project.ts";
import { cellOf } from "./project.ts";

/** Which area a tap landed in. */
export interface AreaHit {
  /** the index the raster holds, one-based as emitted */
  index: number;
  code: string;
  name: string;
}

export interface AreaRow {
  code: string;
  name: string;
  msoaName: string | null;
  crimeDecile: number | null;
  imdDecile: number | null;
  offences: number | null;
  connectivity: number | null;
  medianPrice: number | null;
}

export interface NearestSchool {
  name: string;
  grade: string | null;
  inspected: number | null;
  metres: number;
  letter: string;
}

export interface NearestStation {
  name: string;
  lines: string;
  mode: string;
  metres: number;
}

/** How far out a tap will look for an area before calling it outside London. */
const SEARCH_CELLS = 4;

/** How far round the tapped cell the areas whose outline might hold it are sought. */
const CANDIDATE_CELLS = 2;

/** An area's polygons, each a list of rings of [lon, lat]. */
type Rings = number[][][][];

export class Areas {
  private readonly manifest: Manifest;
  private readonly projector: Projector;
  private readonly cell: Uint16Array;
  private readonly data: Data;
  private figures: AreaFigures | null = null;
  private outlines: AreaOutlines | null = null;
  private readonly decoded = new Map<number, Rings>();

  constructor(manifest: Manifest, projector: Projector, cell: Uint16Array, data: Data) {
    this.manifest = manifest;
    this.projector = projector;
    this.cell = cell;
    this.data = data;
  }

  /**
   * The area at a lon/lat, or null outside London.
   *
   * A tap on the river, a reservoir or a railway yard lands on a cell no LSOA
   * covers, so the search widens by a ring at a time rather than reporting
   * nothing: the reader pointed at somewhere, and the nearest area that has an
   * answer is the honest reading of where they pointed.
   *
   * Before that, if the outlines are loaded, the areas near the tap are tested
   * against the tap itself, so a tap near a border opens the area it is in
   * rather than the one owning the nearest cell centre.
   */
  resolveAt(lon: number, lat: number): AreaHit | null {
    const point = this.projector.toOsgb(lon, lat);
    if (!point) return null;
    const grid = this.manifest.areas.grid;
    const centre = cellOf(grid, point.x, point.y);
    if (centre < 0) return null;
    const cx = centre % grid.nx;
    const cy = Math.floor(centre / grid.nx);
    const exact = this.containing(lon, lat, cx, cy);
    if (exact !== null) return this.hit(exact);
    for (let ring = 0; ring <= SEARCH_CELLS; ring++) {
      for (let dy = -ring; dy <= ring; dy++) {
        for (let dx = -ring; dx <= ring; dx++) {
          if (ring > 0 && Math.max(Math.abs(dx), Math.abs(dy)) !== ring) continue;
          const x = cx + dx;
          const y = cy + dy;
          if (x < 0 || y < 0 || x >= grid.nx || y >= grid.ny) continue;
          const index = this.cell[y * grid.nx + x]!;
          if (index > 0) return this.hit(index);
        }
      }
    }
    return null;
  }

  /** The area near a cell whose outline holds the point, or null. */
  private containing(lon: number, lat: number, cx: number, cy: number): number | null {
    if (!this.outlines) return null;
    const grid = this.manifest.areas.grid;
    const tried = new Set<number>();
    for (let ring = 0; ring <= CANDIDATE_CELLS; ring++) {
      for (let dy = -ring; dy <= ring; dy++) {
        for (let dx = -ring; dx <= ring; dx++) {
          if (ring > 0 && Math.max(Math.abs(dx), Math.abs(dy)) !== ring) continue;
          const x = cx + dx;
          const y = cy + dy;
          if (x < 0 || y < 0 || x >= grid.nx || y >= grid.ny) continue;
          const index = this.cell[y * grid.nx + x]!;
          if (index === 0 || tried.has(index)) continue;
          tried.add(index);
          const rings = this.ringsOf(index);
          if (rings && rings.some((polygon) => insidePolygon(polygon, lon, lat))) return index;
        }
      }
    }
    return null;
  }

  private ringsOf(index: number): Rings | null {
    const cached = this.decoded.get(index);
    if (cached) return cached;
    const polygons = this.outlines?.[index - 1];
    if (!polygons) return null;
    const rings = polygons.map((polygon) =>
      polygon.map((ring) => decodePolyline(ring).map((p) => [p.lon, p.lat])));
    this.decoded.set(index, rings);
    return rings;
  }

  private hit(index: number): AreaHit | null {
    const figures = this.figures;
    if (!figures) return { index, code: "", name: "" };
    const at = index - 1;
    const code = figures.code[at];
    if (code === undefined) return null;
    return { index, code, name: figures.name[at] ?? code };
  }

  /** The index of an area by its LSOA code, for a link that named one. */
  indexOf(code: string): number | null {
    const at = this.figures?.code.indexOf(code) ?? -1;
    return at < 0 ? null : at + 1;
  }

  /** Everything published about one area, or null before the figures arrive. */
  rowFor(index: number): AreaRow | null {
    const figures = this.figures;
    if (!figures) return null;
    const at = index - 1;
    const code = figures.code[at];
    if (code === undefined) return null;
    return {
      code,
      name: figures.name[at] ?? code,
      msoaName: figures.msoa_name[at] ?? null,
      crimeDecile: figures.crime_decile[at] ?? null,
      imdDecile: figures.imd_decile[at] ?? null,
      offences: figures.offences[at] ?? null,
      connectivity: figures.connectivity[at] ?? null,
      medianPrice: figures.median_price[at] ?? null,
    };
  }

  /** One area's outline, decoded from its polylines only when it is drawn. */
  outlineFor(index: number): GeoJSON.Feature | null {
    const coordinates = this.ringsOf(index);
    if (!coordinates) return null;
    return { type: "Feature", properties: {},
             geometry: { type: "MultiPolygon", coordinates } };
  }

  /** Load the figures and outlines if they are not in hand yet. */
  async ready(): Promise<void> {
    const [figures, outlines] = await Promise.all([this.data.figures(), this.data.outlines()]);
    this.figures = figures;
    this.outlines = outlines;
  }

  /**
   * The closest school in the phase the reader has chosen.
   *
   * The same list the pins are drawn from, so the line and the map cannot
   * disagree about which school is nearest.
   */
  nearestSchool(schools: Schools, lon: number, lat: number, phase: string): NearestSchool | null {
    let best: NearestSchool | null = null;
    for (let i = 0; i < schools.lon.length; i++) {
      if (phase === "primary" && !schools.primary[i]) continue;
      if (phase === "secondary" && !schools.secondary[i]) continue;
      const away = metresBetween(lon, lat, schools.lon[i]!, schools.lat[i]!);
      if (best && away >= best.metres) continue;
      const gradeIndex = schools.grade_i[i];
      const grade = gradeIndex === undefined ? null : schools.grades[gradeIndex] ?? null;
      best = {
        name: schools.name[schools.name_i[i]!] ?? "a school",
        grade,
        inspected: schools.inspected[i] ?? null,
        metres: away,
        letter: schools.letter[i] ?? "·",
      };
    }
    return best;
  }

  nearestStation(stations: { lon: number[]; lat: number[]; name: string[]; lines: string[];
                             mode: string[] }, lon: number, lat: number): NearestStation | null {
    let best: NearestStation | null = null;
    for (let i = 0; i < stations.lon.length; i++) {
      const away = metresBetween(lon, lat, stations.lon[i]!, stations.lat[i]!);
      if (best && away >= best.metres) continue;
      best = {
        name: stations.name[i] ?? "a station",
        lines: stations.lines[i] ?? "",
        mode: stations.mode[i] ?? "",
        metres: away,
      };
    }
    return best;
  }
}

/** Whether a point is inside a polygon: its outer ring, less any holes. */
function insidePolygon(rings: number[][][], lon: number, lat: number): boolean {
  let inside = false;
  for (const ring of rings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i]!;
      const [xj, yj] = ring[j]!;
      if ((yi! > lat) !== (yj! > lat) && lon < ((xj! - xi!) * (lat - yi!)) / (yj! - yi!) + xi!) {
        inside = !inside;
      }
    }
  }
  return inside;
}
